import * as THREE from 'three';
import { FeatureFace } from './FeatureFace';
import { FeatureLine } from './FeatureLine';
import { FeaturePoint } from './FeaturePoint';
import { addObject3DToParent } from '../../utils/scene-object/addObject3DToParent';
import { disposeOwnedMaterial } from '../../material/materialOwnership';
import { createTopologyFeatureFaceProxy } from './factory/createTopologyFeatureFaceProxy';
import { createTopologyFeatureLineProxy } from './factory/createTopologyFeatureLineProxy';
import { createCompactIndexedFaceGeometry } from '../utils/createCompactIndexedFaceGeometry';
import type { TopologyFeatureFaceGeometryProvider } from './TopologyFeatureFaceGeometryProvider';
//------ 模型显示模式 开始------
import { FeatureTopologyDisplayEdgesMesh } from './FeatureTopologyDisplayEdgesMesh';
//------ 模型显示模式 结束------

/**
 * 构造 FeatureResultMesh 时所需的最小 meshData 结构。
 *
 * 说明：构造函数内部仅消费 id / metadata / faceTriangleRanges，
 * positions 与 normals 由外部传入的 geometry 承载，此处设为可选，
 * 以便内部克隆方法（cloneForPatternPreview）不必重复传递冗余数据。
 * 存量调用者传入完整 MeshData 时仍满足此类型，无需改动。
 */
type FeatureResultMeshData = Omit<MeshData, 'positions' | 'normals'> & Partial<Pick<MeshData, 'positions' | 'normals'>>;

/**
 * 特征结果体拓扑代理选择模式。
 *
 * 设计说明：
 * 1. 结果体的边/点代理只在当前挂载模式真正需要时才挂入场景；
 * 2. 面选择直接依赖结果体三角面命中与权威快照映射，不再长期把所有面代理挂进场景；
 * 3. 这样可以从底层降低圆角/倒角后大量拓扑代理对象带来的渲染与拾取成本。
 */
export type FeatureTopologySelectionMode = 'none' | 'edge' | 'face' | 'face-edge' | 'vertex' | 'topology';

/**
 * 结果体拓扑代理挂载模式。
 *
 * 注意：当前默认浏览态仍需要 `all` 来维持实体点、线、面的 hover/click 语义。
 * 如果未来取消 `all`，必须先把选择系统改造成不依赖场景代理的拓扑拾取。
 */
export type FeatureTopologyProxyMountMode = FeatureTopologySelectionMode | 'all';

/**
 * 构建三维点。
 *
 * @param point 元组坐标。
 * @returns Three 向量。
 */
function buildVector3(point: [number, number, number]): THREE.Vector3 {
  return new THREE.Vector3(point[0], point[1], point[2]);
}

/**
 * 释放对象材质。
 *
 * @param object 目标对象。
 */
function disposeObjectMaterial(object: THREE.Object3D): void {
  const material = Reflect.get(object, 'material') as THREE.Material | THREE.Material[] | undefined;
  disposeOwnedMaterial(material);
}

/**
 * 判断对象是否为特征结果体。
 *
 * @param value 待判断对象。
 * @returns 是否为特征结果体。
 */
export function isFeatureResultMesh(value: THREE.Object3D): value is FeatureResultMesh {
  if (!value || typeof value !== 'object') {
    return false;
  }
  return Reflect.get(value, 'isFeatureResultMesh') === true;
}

/**
 * 判断当前模式是否需要挂载边代理。
 *
 * @param mode 代理选择模式。
 * @returns 是否需要边代理。
 */
function shouldAttachEdgeProxies(mode: FeatureTopologyProxyMountMode): boolean {
  return mode === 'all' || mode === 'edge' || mode === 'face-edge' || mode === 'topology';
}

/**
 * 判断当前模式是否需要挂载面代理。
 *
 * @param mode 代理选择模式。
 * @returns 是否需要面代理。
 */
function shouldAttachFaceProxies(mode: FeatureTopologyProxyMountMode): boolean {
  return mode === 'all' || mode === 'face' || mode === 'face-edge' || mode === 'topology';
}

/**
 * 判断当前模式是否需要挂载点代理。
 *
 * @param mode 代理选择模式。
 * @returns 是否需要点代理。
 */
function shouldAttachPointProxies(mode: FeatureTopologyProxyMountMode): boolean {
  return mode === 'all' || mode === 'vertex' || mode === 'topology';
}

/**
 * 判断顶点快照是否应该暴露为可选点代理。
 *
 * 设计说明：
 * 闭合圆/椭圆边可能使用曲线采样点作为边引用端点，这些点不是模型的真实拓扑顶点；
 * 因此渲染层必须在统一入口过滤，避免圆柱端面轮廓线上出现 SolidWorks 不存在的可选点。
 *
 * @param vertex 顶点快照。
 * @returns 是否允许创建 FeaturePoint 代理。
 */
function shouldAttachPointProxyForVertex(vertex: TopologySnapshotVertex): boolean {
  return vertex.role !== 'curve-reference';
}

/**
 * 特征结果体封装。
 *
 * 设计说明：
 * 1. 结果体只消费 worker 提供的权威拓扑快照，不再从三角网格反推业务拓扑真相；
 * 2. 面选择通过“结果体三角命中 -> 权威快照面索引”完成，边/点代理按当前挂载模式惰性挂载；
 * 3. 这样既能保持选择语义稳定，也能避免圆角后大量代理对象常驻场景导致卡顿。
 */
export class FeatureResultMesh extends THREE.Mesh {
  /** 运行时类型标记。 */
  public readonly isFeatureResultMesh: boolean = true;

  /** 网格对象 ID。 */
  public readonly objectId: string;

  /** 面三角形范围。 */
  public readonly faceTriangleRanges?: [number, number][];

  /** 结果体元数据。 */
  private readonly featureMetadata: Record<string, unknown>;

  /** 权威拓扑快照。 */
  private topologySnapshot: TopologySnapshot | null = null;

  /** 拓扑视觉快照版本，用于识别同一 mesh 上的拓扑代理是否已更新。 */
  private topologyVisualRevision = 0;

  /** 当前拓扑快照失效时需要同步归还借用引用的单次订阅。 */
  private readonly topologyInvalidationListeners = new Set<() => void>();

  /** 三角形到面索引的运行时映射。 */
  private readonly triangleToFaceIndexMap: Map<number, number> = new Map();

  /** 权威拓扑顶点索引映射，避免把 topology index 误当数组下标。 */
  private readonly topologyVertexByIndex: Map<number, TopologySnapshotVertex> = new Map();

  /** 权威拓扑边索引映射，避免热路径反复线性查找。 */
  private readonly topologyEdgeByIndex: Map<number, TopologySnapshotEdge> = new Map();

  /** 权威拓扑面索引映射，确保 BVH 改写三角顺序后仍按快照索引命中。 */
  private readonly topologyFaceByIndex: Map<number, TopologySnapshotFace> = new Map();

  /** 已缓存的边代理。 */
  private readonly featureLineMap: Map<number, FeatureLine> = new Map();

  /** 已缓存的点代理。 */
  private readonly featurePointMap: Map<number, FeaturePoint> = new Map();

  /** 已缓存的面代理。 */
  private readonly featureFaceMap: Map<number, FeatureFace> = new Map();

  /** 面代理显示几何 provider，用于把导入模型的视觉网格与拓扑语义解耦。 */
  private featureFaceGeometryProvider: TopologyFeatureFaceGeometryProvider | null = null;

  /** 当前拓扑代理挂载模式。 */
  private topologySelectionMode: FeatureTopologyProxyMountMode = 'none';

  //------ 模型显示模式 开始------
  /** 是否启用合并后的拓扑显示边。 */
  private topologyDisplayEdgesEnabled = false;

  /** 当前是否因实体替换预览临时压制默认显示边。 */
  private topologyDisplayEdgesSuppressed = false;

  /** 合并后的拓扑显示边对象。 */
  private topologyDisplayEdgesMesh: FeatureTopologyDisplayEdgesMesh | null = null;

  /** 合并后的拓扑显示边颜色。 */
  private topologyDisplayEdgeColor: THREE.ColorRepresentation = 0x000000;
  //------ 模型显示模式 结束------

  /**
   * 构造函数。
   *
   * @param input 输入参数。
   */
  public constructor(input: {
    meshData: FeatureResultMeshData;
    geometry: THREE.BufferGeometry;
    material: THREE.Material | THREE.Material[];
  }) {
    super(input.geometry, input.material);
    this.objectId = input.meshData.metadata?.objectId as string ?? input.meshData.id;
    this.faceTriangleRanges = input.meshData.faceTriangleRanges?.map(([start, end]) => [start, end]);
    this.featureMetadata = {
      ...(input.meshData.metadata ?? {}),
      objectId: this.objectId,
    };
    this.name = this.objectId;
  }

  /**
   * 获取特征元数据。
   *
   * @returns 元数据副本。
   */
  public getFeatureMetadata(): Record<string, unknown> {
    return { ...this.featureMetadata };
  }

  /**
   * 获取面三角形范围。
   *
   * @returns 面三角形范围副本。
   */
  public getFaceTriangleRanges(): [number, number][] | undefined {
    return this.faceTriangleRanges?.map(([start, end]) => [start, end]);
  }

  /**
   * 获取权威拓扑快照。
   *
   * @returns 权威拓扑快照。
   */
  public getTopologySnapshot(): TopologySnapshot | null {
    return this.topologySnapshot;
  }

  /**
   * 读取拓扑视觉快照版本。
   *
   * 设计说明：
   * 设计树特征局部高亮依赖 `topologySnapshot` 中的特征归属。同一个 mesh 实例可能复用 uuid，
   * 但内部拓扑快照已经更新，因此选择态比较键必须能感知这一变化。
   *
   * @returns 当前拓扑视觉快照版本。
   */
  public getTopologyVisualRevision(): number {
    return this.topologyVisualRevision;
  }

  /**
   * 订阅当前拓扑快照的下一次失效，用于派生缓存对称解除宿主与快照引用。
   *
   * 订阅只触发一次；新快照的消费者应重新订阅。普通代理回收、场景脱离和位姿变化不触发，
   * 避免把短期 Hover 缓存误当作过期数据清空。消费者提前淘汰缓存时必须调用返回的解绑入口。
   *
   * @param listener 拓扑清空或替换后执行的同步释放回调。
   * @returns 可重复调用的解绑函数，不释放宿主拥有的资源。
   */
  public onTopologyInvalidated(listener: () => void): () => void {
    this.topologyInvalidationListeners.add(listener);
    return () => { this.topologyInvalidationListeners.delete(listener); };
  }

  /**
   * 设置面代理显示几何 provider。
   *
   * 设计说明：
   * 1. provider 只影响 `FeatureFace` 的显示三角面，不改变拓扑快照、面索引或点线语义；
   * 2. 切换 provider 时必须释放已缓存面代理，避免旧几何继续参与 hover/selected 高亮；
   * 3. 传入 `null` 会恢复为默认的结果体几何兜底策略。
   *
   * @param provider 面代理显示几何 provider。
   */
  public setFeatureFaceGeometryProvider(provider: TopologyFeatureFaceGeometryProvider | null): void {
    if (this.featureFaceGeometryProvider === provider) {
      return;
    }
    const previousProvider = this.featureFaceGeometryProvider;
    this.clearFeatureFaceProxyCache();
    previousProvider?.dispose?.();
    this.featureFaceGeometryProvider = provider;
    this.syncMountedTopologyProxies();
  }

  /**
   * 读取外部面几何 Provider 的有界缓存统计。
   *
   * 该入口只服务资源预算和显式性能检查；普通 Hover 不创建统计对象。
   *
   * @returns Provider 未实现缓存统计时返回 `null`。
   */
  public getFeatureFaceGeometryCacheStats(): ImportedTopologyFaceGeometryCacheStats | null {
    return this.featureFaceGeometryProvider?.getCacheStats?.() ?? null;
  }

  /**
   * 根据面索引读取权威拓扑快照。
   *
   * @param faceIndex 面索引。
   * @returns 面拓扑快照；不存在时返回 `null`。
   */
  public getTopologyFaceSnapshotByIndex(faceIndex: number): TopologySnapshotFace | null {
    return this.topologyFaceByIndex.get(faceIndex) ?? null;
  }

  /**
   * 根据边索引读取权威拓扑快照。
   *
   * @param edgeIndex 边索引。
   * @returns 边拓扑快照；不存在时返回 `null`。
   */
  public getTopologyEdgeSnapshotByIndex(edgeIndex: number): TopologySnapshotEdge | null {
    return this.topologyEdgeByIndex.get(edgeIndex) ?? null;
  }

  /**
   * 根据顶点索引读取权威拓扑快照。
   *
   * @param vertexIndex 顶点索引。
   * @returns 顶点拓扑快照；不存在时返回 `null`。
   */
  public getTopologyVertexSnapshotByIndex(vertexIndex: number): TopologySnapshotVertex | null {
    return this.topologyVertexByIndex.get(vertexIndex) ?? null;
  }

  /**
   * 根据结果体三角形索引读取权威面快照。
   *
   * 设计说明：导入模型二阶段拾取只需要先确定面索引，不应为了读取拓扑真相提前创建
   * `FeatureFace` 显示代理。该入口保持三角形映射和代理物化职责分离。
   *
   * @param triangleIndex 结果体三角形索引。
   * @returns 面拓扑快照；不存在时返回 `null`。
   */
  public getTopologyFaceSnapshotByTriangleIndex(triangleIndex: number): TopologySnapshotFace | null {
    const faceIndex = this.triangleToFaceIndexMap.get(triangleIndex);
    return typeof faceIndex === 'number'
      ? this.getTopologyFaceSnapshotByIndex(faceIndex)
      : null;
  }

  /**
   * 设置当前结果体的拓扑代理挂载模式。
   *
   * @param mode 代理挂载模式。
   */
  public setTopologySelectionMode(mode: FeatureTopologyProxyMountMode): void {
    if (this.topologySelectionMode === mode) {
      return;
    }
    this.topologySelectionMode = mode;
    this.syncMountedTopologyProxies();
  }

  //------ 模型显示模式 开始------
  /**
   * 设置拓扑显示边是否启用。
   *
   * 设计说明：
   * 1. 该入口只表达显示模式对默认可见边的需求，不改变点、面、边选择代理策略；
   * 2. 默认显示边使用一个合并 `LineSegments`，避免把每条边都常驻为 `FeatureLine`；
   * 3. 点击选边仍然通过 `attachFeatureLineByIndex()` 按需创建真实拓扑代理。
   *
   * @param enabled 是否启用拓扑显示边。
   * @param color 拓扑显示边颜色。
   */
  public setTopologyDisplayEdgesEnabled(
    enabled: boolean,
    color: THREE.ColorRepresentation = 0x000000,
  ): void {
    const shouldUpdateColor = this.topologyDisplayEdgeColor !== color;
    this.topologyDisplayEdgeColor = color;

    if (this.topologyDisplayEdgesEnabled === enabled) {
      if (this.isTopologyDisplayEdgesStateDirty()) {
        this.syncTopologyDisplayEdges();
      } else if (shouldUpdateColor) {
        this.topologyDisplayEdgesMesh?.setDisplayEdgeColor(color);
      }
      return;
    }

    this.topologyDisplayEdgesEnabled = enabled;
    this.syncTopologyDisplayEdges();
  }

  /**
   * 判断当前结果体的权威拓扑显示边是否真实可见。
   *
   * 该查询供草图模型边捕捉等派生交互使用，确保候选与当前显示模式一致；实体替换预览期间
   * 即使保留了显示边配置，也必须按 suppressed 状态返回不可见。
   *
   * @returns 默认拓扑显示边已启用且未被临时压制时返回 true。
   */
  public isTopologyDisplayEdgesVisible(): boolean {
    return this.topologyDisplayEdgesEnabled && !this.topologyDisplayEdgesSuppressed;
  }

  /**
   * 设置实体替换预览期间的默认显示边抑制状态。
   *
   * 设计说明：
   * 1. 该状态只影响浏览态合并黑边，不改变 FeatureLine/Face/Point 交互代理；
   * 2. 显示模式的 enabled 与颜色配置继续保留，退出预览后可按最新模式原样恢复；
   * 3. 导入实体 pick host 与手工结果体都使用同一个 FeatureResultMesh 入口。
   *
   * @param suppressed 是否临时压制默认显示边。
   */
  public setTopologyDisplayEdgesSuppressed(suppressed: boolean): void {
    if (this.topologyDisplayEdgesSuppressed === suppressed) {
      return;
    }
    this.topologyDisplayEdgesSuppressed = suppressed;
    this.syncTopologyDisplayEdges();
  }
  //------ 模型显示模式 结束------

  /**
   * 根据面索引读取面代理。
   *
   * @param faceIndex 面索引。
   * @returns 面代理；未命中时返回 `null`。
   */
  public getFeatureFaceByIndex(faceIndex: number): FeatureFace | null {
    if (!this.topologySnapshot) {
      return null;
    }
    const cachedFace = this.featureFaceMap.get(faceIndex) ?? null;
    if (cachedFace) {
      return cachedFace;
    }

    const faceSnapshot = this.topologyFaceByIndex.get(faceIndex);
    if (!faceSnapshot) {
      return null;
    }

    const geometry = this.createFeatureFaceProxyGeometry(faceSnapshot);
    if (!geometry) {
      return null;
    }

    const featureFace = createTopologyFeatureFaceProxy({
      objectId: this.objectId,
      faceIndex: faceSnapshot.index,
      subEntityId: faceSnapshot.subEntityId,
      persistentRef: faceSnapshot.ref,
      triangleRange: faceSnapshot.triangleRange,
      adjacentEdgeIndices: faceSnapshot.adjacentEdgeIndices,
      geometry,
      localNormal: faceSnapshot.localNormal,
      localPoint: faceSnapshot.localPoint,
      surfaceKind: faceSnapshot.surfaceKind,
    });
    featureFace.setOwningResultMesh(this);
    featureFace.setFeatureMetadata(this.featureMetadata);
    featureFace.setBoundaryLines(
      faceSnapshot.adjacentEdgeIndices
        .map((edgeIndex) => this.featureLineMap.get(edgeIndex) ?? null)
        .filter((featureLine): featureLine is FeatureLine => featureLine instanceof FeatureLine),
    );
    this.featureFaceMap.set(faceIndex, featureFace);
    return featureFace;
  }


  /**
   * 创建面代理显示几何。
   *
   * 设计说明：
   * 1. 优先询问外部 provider，让导入模型可直接复用 GLB 可见网格的高质量三角面；
   * 2. provider 无法处理时回退到 `FeatureResultMesh` 自身几何，保持草图/建模结果与旧导入数据兼容；
   * 3. 该函数只负责几何来源选择，不参与拓扑索引判定，避免再次引入 BVH 改写索引导致的错面问题。
   *
   * @param faceSnapshot 权威拓扑面快照。
   * @returns 面代理几何；无法构建时返回 `null`。
   */
  private createFeatureFaceProxyGeometry(faceSnapshot: TopologySnapshotFace): THREE.BufferGeometry | null {
    const providerGeometry = this.featureFaceGeometryProvider?.createFaceGeometry({
      host: this,
      face: faceSnapshot,
    }) ?? null;
    if (providerGeometry) {
      return providerGeometry;
    }

    /*
     * WebGPU 对顶点缓冲绑定比 WebGL 更严格，面代理不能共享结果体 Mesh 的 BufferAttribute。
     * 这里使用紧凑独立几何，保证 hover/selected 材质切换不会影响原始结果体的渲染管线。
     */
    return createCompactIndexedFaceGeometry({
      sourceGeometry: this.geometry,
      triangleRange: faceSnapshot.triangleRange,
    });
  }

  /**
   * 根据三角形索引解析面代理。
   *
   * @param triangleIndex 三角形索引。
   * @returns 面代理；未命中时返回 `null`。
   */
  public getFeatureFaceByTriangleIndex(triangleIndex: number): FeatureFace | null {
    const faceIndex = this.triangleToFaceIndexMap.get(triangleIndex);
    if (typeof faceIndex !== 'number') {
      return null;
    }
    return this.getFeatureFaceByIndex(faceIndex);
  }

  //------ 设计树程序化特征移入高亮 开始------
  /**
   * 读取当前结果体全部特征线代理快照。
   *
   * 设计说明：
   * 1. 设计树移入特征节点时，需要从结果体统一读取该特征的全部边代理；
   * 2. 这里只暴露只读快照，不允许外层直接操作内部缓存结构；
   * 3. 后续若边代理缓存策略调整，调用方仍可复用同一查询入口。
   *
   * @returns 全部特征线代理快照。
   */
  public getAllFeatureLines(): FeatureLine[] {
    return Array.from(this.featureLineMap.values());
  }

  /**
   * 判断当前结果体是否包含指定特征的拓扑贡献。
   *
   * 设计说明：
   * 1. 设计树特征节点高亮必须以 topologySnapshot 的 owningFeatureId 为权威；
   * 2. 相交合并后的实体会同时携带多个历史特征贡献，不能仅凭实体 ID 判断高亮范围；
   * 3. 该方法只读取快照，不挂载点线面代理，避免刷新树和 hover 热路径产生额外 three.js 对象。
   *
   * @param featureId 特征 ID。
   * @returns 当前结果体是否包含该特征贡献。
   */
  public hasFeatureContribution(featureId: string): boolean {
    const normalizedFeatureId = featureId.trim();
    if (!this.topologySnapshot || normalizedFeatureId.length <= 0) {
      return false;
    }

    return this.topologySnapshot.faces.some((face) => face.owningFeatureId === normalizedFeatureId)
      || this.topologySnapshot.edges.some((edge) => edge.owningFeatureId === normalizedFeatureId);
  }

  /**
   * 读取指定特征贡献的边代理。
   *
   * 设计说明：
   * 1. 合并后的结果体会同时携带多个历史特征的拓扑归属，设计树选择必须按 featureId 过滤；
   * 2. 这里只挂载匹配的边代理，不切换全局 topology mode，避免污染拾取、测量、倒角和圆角命令；
   * 3. 旧数据没有 owningFeatureId 时不回退全量高亮，避免再次把合并实体整体染色。
   *
   * @param featureId 特征 ID。
   * @returns 匹配该特征归属的边代理列表。
   */
  public getFeatureLinesByOwningFeatureId(featureId: string): FeatureLine[] {
    const normalizedFeatureId = featureId.trim();
    if (!this.topologySnapshot || normalizedFeatureId.length <= 0) {
      return [];
    }

    return this.topologySnapshot.edges
      .filter((edge) => edge.owningFeatureId === normalizedFeatureId)
      .map((edge) => this.attachFeatureLineByIndex(edge.index))
      .filter((featureLine): featureLine is FeatureLine => featureLine instanceof FeatureLine);
  }

  /**
   * 读取指定特征贡献面的边界线代理。
   *
   * 设计说明：
   * 1. 设计树选中特征时，SolidWorks 展示的是该特征贡献面的可见范围；
   * 2. 边界线应由匹配面上的 `adjacentEdgeIndices` 推导，而不是直接染所有同 owner 的边；
   * 3. 通过 edge index 去重，避免同一条边同时属于多个贡献面时重复创建视觉状态。
   *
   * @param featureId 特征 ID。
   * @returns 匹配特征贡献面的边界线代理列表。
   */
  public getFeatureBoundaryLinesByOwningFeatureId(featureId: string): FeatureLine[] {
    const edgeIndices = new Set<number>();
    this.getFeatureFacesByOwningFeatureId(featureId).forEach((featureFace) => {
      featureFace.adjacentEdgeIndices.forEach((edgeIndex) => {
        edgeIndices.add(edgeIndex);
      });
    });

    return Array.from(edgeIndices)
      .map((edgeIndex) => this.attachFeatureLineByIndex(edgeIndex))
      .filter((featureLine): featureLine is FeatureLine => featureLine instanceof FeatureLine);
  }

  /**
   * 读取当前结果体全部特征面代理快照。
   *
   * 设计说明：
   * 1. 设计树移入特征节点时，需要从结果体统一读取该特征的全部面代理；
   * 2. 这里同样只返回快照数组，避免把内部 Map 直接泄漏给上层；
   * 3. 默认视觉状态因此无需知道结果体内部缓存实现。
   *
   * @returns 全部特征面代理快照。
   */
  public getAllFeatureFaces(): FeatureFace[] {
    return Array.from(this.featureFaceMap.values());
  }

  /**
   * 读取指定特征贡献的面代理。
   *
   * 设计说明：
   * 1. SolidWorks 选中特征节点时表达的是该特征对最终实体的贡献范围，不是整个合并后的实体；
   * 2. 面范围以 worker 生成的 topologySnapshot.faces[].owningFeatureId 为唯一依据；
   * 3. 未携带归属的旧数据不做全量兜底，避免旧项目在特征树选择时继续出现整实体误高亮。
   *
   * @param featureId 特征 ID。
   * @returns 匹配该特征归属的面代理列表。
   */
  public getFeatureFacesByOwningFeatureId(featureId: string): FeatureFace[] {
    const normalizedFeatureId = featureId.trim();
    if (!this.topologySnapshot || normalizedFeatureId.length <= 0) {
      return [];
    }

    return this.topologySnapshot.faces
      .filter((face) => face.owningFeatureId === normalizedFeatureId)
      .map((face) => this.attachFeatureFaceByIndex(face.index))
      .filter((featureFace): featureFace is FeatureFace => featureFace instanceof FeatureFace);
  }

  //------ 设计树程序化特征移入高亮 结束------

  //------ 线性零部件阵列稳定引用 开始------
  /**
   * 读取当前结果体全部特征点代理快照。
   *
   * 设计说明：
   * 1. 动态点代理可能已经被交互链路创建但未挂回场景树；
   * 2. 特征编辑恢复旧定义时需要从结果体缓存兜底查找这些代理；
   * 3. 返回快照数组，避免外层直接依赖内部 Map 生命周期。
   *
   * @returns 全部特征点代理快照。
   */
  public getAllFeaturePoints(): FeaturePoint[] {
    return Array.from(this.featurePointMap.values());
  }
  //------ 线性零部件阵列稳定引用 结束------

  /**
   * 按边索引读取边代理。
   *
   * 设计说明：
   * 1. 导入件命中的边必须直接复用真正的 `FeatureLine / FeatureCurve` 对象；
   * 2. 是否挂载到场景树由当前代理挂载模式或二阶段交互调用方决定；
   * 3. 统一复用内部缓存，避免 hover 热路径重复创建几何与材质。
   *
   * @param edgeIndex 边索引。
   * @returns 边代理；不存在时返回 `null`。
   */
  public getFeatureLineByIndex(edgeIndex: number): FeatureLine | null {
    if (!this.topologySnapshot) {
      return null;
    }
    if (!this.topologyEdgeByIndex.has(edgeIndex)) {
      return null;
    }
    return this.ensureFeatureLineProxy(edgeIndex);
  }

  /**
   * 按顶点索引读取点代理。
   *
   * @param vertexIndex 顶点索引。
   * @returns 点代理；不存在时返回 `null`。
   */
  public getFeaturePointByIndex(vertexIndex: number): FeaturePoint | null {
    if (!this.topologySnapshot) {
      return null;
    }
    const vertexSnapshot = this.topologyVertexByIndex.get(vertexIndex);
    if (!vertexSnapshot || !shouldAttachPointProxyForVertex(vertexSnapshot)) {
      return null;
    }
    return this.ensureFeaturePointProxy(vertexIndex);
  }

  /**
   * 挂载指定边代理到当前结果体。
   *
   * 设计说明：
   * 1. 导入件链路需要让轮廓线本身就是 `FeatureLine` 代理对象，而不是额外的 overlay 线框；
   * 2. 这里统一提供“读取并挂载”的底层入口，避免 imported-model 在外部重复判断 parent/add 逻辑；
   * 3. 若边不存在则返回 `null`，由上层选择链按宿主面或对象级回退。
   *
   * @param edgeIndex 边索引。
   * @returns 已挂载的边代理；不存在时返回 `null`。
   */
  public attachFeatureLineByIndex(edgeIndex: number): FeatureLine | null {
    const featureLine = this.getFeatureLineByIndex(edgeIndex);
    if (!featureLine) {
      return null;
    }
    if (featureLine.parent !== this) {
      addObject3DToParent(featureLine, this);
      this.refreshFeatureFaceBoundaryLines();
    }
    return featureLine;
  }

  /**
   * 为指定面代理重新绑定边界线对象。
   *
   * 设计说明：
   * 1. 拖动装配体零件后的局部时序里，面代理可能仍然存活，但其 `boundaryLines` 缓存已被清空；
   * 2. 面代理本身保存了稳定的 `adjacentEdgeIndices`，因此可以直接回到结果体拓扑快照重新取回权威边代理；
   * 3. 双面建轴重算应优先消费真实边界线，而不是长期依赖面几何顶点兜底。
   *
   * @param featureFace 面代理对象。
   */
  public rebindFeatureFaceBoundaryLines(featureFace: FeatureFace): void {
    featureFace.setBoundaryLines(
      featureFace.adjacentEdgeIndices
        .map((edgeIndex) => this.getFeatureLineByIndex(edgeIndex))
        .filter((featureLine): featureLine is FeatureLine => featureLine instanceof FeatureLine),
    );
  }

  /**
   * 挂载指定面代理到当前结果体。
   *
   * 设计说明：
   * 1. 面代理默认不需要常驻渲染，只有在 hover / selected 进入面语义时才按需挂载；
   * 2. 这样可以保留 `FeatureFace` 统一交互语义，同时避免大装配把所有面代理都压进场景；
   * 3. 面代理一旦挂载，边界线引用需要立即刷新，保证后续面-边联动使用同一批 `FeatureLine` 对象。
   *
   * @param faceIndex 面索引。
   * @returns 已挂载的面代理；不存在时返回 `null`。
   */
  public attachFeatureFaceByIndex(faceIndex: number): FeatureFace | null {
    const featureFace = this.getFeatureFaceByIndex(faceIndex);
    if (!featureFace) {
      return null;
    }
    if (featureFace.parent !== this) {
      addObject3DToParent(featureFace, this);
      this.refreshFeatureFaceBoundaryLines();
    }
    return featureFace;
  }

  /**
   * 按三角形索引挂载面代理。
   *
   * @param triangleIndex 三角形索引。
   * @returns 已挂载的面代理；未命中时返回 `null`。
   */
  public attachFeatureFaceByTriangleIndex(triangleIndex: number): FeatureFace | null {
    const faceIndex = this.triangleToFaceIndexMap.get(triangleIndex);
    if (typeof faceIndex !== 'number') {
      return null;
    }
    return this.attachFeatureFaceByIndex(faceIndex);
  }

  /**
   * 挂载指定点代理到当前结果体。
   *
   * 设计说明：
   * 1. 顶点默认不应常驻显示，否则会把导入件变成一片可见点云；
   * 2. 但顶点一旦进入顶点过滤/选择链，仍然必须复用真正的 `FeaturePoint` 代理对象；
   * 3. 因此这里提供按需挂载入口，供导入件拾取链在命中局部候选后再暴露点代理。
   *
   * @param vertexIndex 顶点索引。
   * @returns 已挂载的点代理；不存在时返回 `null`。
   */
  public attachFeaturePointByIndex(vertexIndex: number): FeaturePoint | null {
    const featurePoint = this.getFeaturePointByIndex(vertexIndex);
    if (!featurePoint) {
      return null;
    }
    if (featurePoint.parent !== this) {
      addObject3DToParent(featurePoint, this);
    }
    return featurePoint;
  }

  /**
   * 仅保留当前交互仍然需要的临时拓扑代理对象。
   *
   * 设计说明：
   * 1. imported-model 的点线面代理由交互链路按需创建，离开当前交互状态后必须统一回收；
   * 2. 面代理、点代理和按需边代理如果不在统一入口回收，
   *    用户每扫过一次面或点，宿主下都会残留新的交互对象，
   *    最终让点击、滚轮缩放和后续 hover 都越来越卡；
   * 3. 该方法只回收“不再被当前交互状态引用”的临时代理，显式选择模式要求常驻的代理仍由
   *    `topologySelectionMode` 保留，避免破坏原生结果体的既有行为。
   *
   * @param retainedObjects 当前仍应保留的点线面代理集合。
   */
  public retainTransientTopologyProxyObjects(retainedObjects: Array<FeatureLine | FeatureFace | FeaturePoint>): void {
    const retainedObjectIds = new Set(retainedObjects.map((object) => object.uuid));

    this.featureLineMap.forEach((featureLine, edgeIndex) => {
      if (
        retainedObjectIds.has(featureLine.uuid)
        || shouldAttachEdgeProxies(this.topologySelectionMode)
      ) {
        return;
      }
      this.disposeFeatureLineProxy(edgeIndex, featureLine);
    });

    this.featureFaceMap.forEach((featureFace, faceIndex) => {
      if (retainedObjectIds.has(featureFace.uuid) || shouldAttachFaceProxies(this.topologySelectionMode)) {
        return;
      }
      this.disposeFeatureFaceProxy(faceIndex, featureFace);
    });

    this.featurePointMap.forEach((featurePoint, vertexIndex) => {
      if (retainedObjectIds.has(featurePoint.uuid) || shouldAttachPointProxies(this.topologySelectionMode)) {
        return;
      }
      this.disposeFeaturePointProxy(vertexIndex, featurePoint);
    });

    this.refreshFeatureFaceBoundaryLines();
  }

  /**
   * 同步边、面、点代理对象。
   *
   * @param snapshot 权威拓扑快照。
   */
  public syncTopologyVisuals(snapshot: TopologySnapshot | null): void {
    this.clearTopologyVisuals();
    this.topologySnapshot = snapshot;
    if (!snapshot) {
      return;
    }

    /**
     * BRep 快照中的 index 是权威拓扑索引，不保证与数组下标一致。
     * 这里在同步阶段一次性建表，后续 hover/click 热路径只做 O(1) 查询。
     */
    snapshot.vertices.forEach((vertex) => {
      this.topologyVertexByIndex.set(vertex.index, vertex);
    });
    snapshot.edges.forEach((edge) => {
      this.topologyEdgeByIndex.set(edge.index, edge);
    });
    snapshot.faces.forEach((face) => {
      this.topologyFaceByIndex.set(face.index, face);
      for (let triangleIndex = face.triangleRange[0]; triangleIndex < face.triangleRange[1]; triangleIndex += 1) {
        this.triangleToFaceIndexMap.set(triangleIndex, face.index);
      }
    });

    //------ 模型显示模式 开始------
    this.syncTopologyDisplayEdges();
    //------ 模型显示模式 结束------
    this.syncMountedTopologyProxies();
  }

  /**
   * 清理拓扑可视化对象。
   */
  public clearTopologyVisuals(): void {
    //------ 模型显示模式 开始------
    this.disposeTopologyDisplayEdgesMesh();
    //------ 模型显示模式 结束------
    this.disposeAllFeatureLineProxies();
    this.disposeAllFeaturePointProxies();
    this.disposeAllFeatureFaceProxies();
    this.topologySnapshot = null;
    this.triangleToFaceIndexMap.clear();
    this.topologyVertexByIndex.clear();
    this.topologyEdgeByIndex.clear();
    this.topologyFaceByIndex.clear();
    this.topologyVisualRevision += 1;

    // 普通结果体没有派生订阅时不额外分配通知数组。
    if (this.topologyInvalidationListeners.size === 0) return;
    // 先脱离本轮订阅，再通知消费者；回调中重新订阅的新快照不能被旧轮次误清理。
    const invalidationListeners = Array.from(this.topologyInvalidationListeners);
    this.topologyInvalidationListeners.clear();
    invalidationListeners.forEach((listener) => listener());
  }

  /**
   * 同步当前模式所需的拓扑代理。
   */
  private syncMountedTopologyProxies(): void {
    if (!this.topologySnapshot) {
      this.disposeAllFeatureLineProxies();
      this.disposeAllFeaturePointProxies();
      this.disposeAllFeatureFaceProxies();
      return;
    }

    if (shouldAttachEdgeProxies(this.topologySelectionMode)) {
      this.topologySnapshot.edges.forEach((edge) => {
        const featureLine = this.ensureFeatureLineProxy(edge.index);
        if (featureLine.parent !== this) {
          addObject3DToParent(featureLine, this);
        }
      });
      this.refreshFeatureFaceBoundaryLines();
    } else {
      this.disposeAllFeatureLineProxies();
    }

    if (shouldAttachFaceProxies(this.topologySelectionMode)) {
      this.topologySnapshot.faces.forEach((face) => {
        const featureFace = this.getFeatureFaceByIndex(face.index);
        if (featureFace && featureFace.parent !== this) {
          addObject3DToParent(featureFace, this);
        }
      });
      this.refreshFeatureFaceBoundaryLines();
    } else {
      this.clearFeatureFaceProxyCache();
    }

    if (shouldAttachPointProxies(this.topologySelectionMode)) {
      this.topologySnapshot.vertices
        .filter(shouldAttachPointProxyForVertex)
        .forEach((vertex) => {
          const featurePoint = this.ensureFeaturePointProxy(vertex.index);
          if (featurePoint.parent !== this) {
            addObject3DToParent(featurePoint, this);
          }
        });
    } else {
      this.disposeAllFeaturePointProxies();
    }
  }

  /**
   * 读取或创建边代理。
   *
   * @param edgeIndex 边索引。
   * @returns 边代理。
   */
  private ensureFeatureLineProxy(edgeIndex: number): FeatureLine {
    const cachedLine = this.featureLineMap.get(edgeIndex);
    if (cachedLine) {
      return cachedLine;
    }

    if (!this.topologySnapshot) {
      throw new Error(`结果体缺少拓扑快照，无法创建边代理: ${this.objectId}`);
    }
    const edgeSnapshot = this.topologyEdgeByIndex.get(edgeIndex);
    if (!edgeSnapshot) {
      throw new Error(`未找到边拓扑快照，无法创建边代理: ${this.objectId}#edge:${edgeIndex}`);
    }
    const vertexA = this.topologyVertexByIndex.get(edgeSnapshot.vertexAIndex);
    const vertexB = this.topologyVertexByIndex.get(edgeSnapshot.vertexBIndex);
    if (!vertexA || !vertexB) {
      throw new Error(`边拓扑顶点缺失，无法创建边代理: ${this.objectId}#edge:${edgeIndex}`);
    }

    const sampledPoints = edgeSnapshot.sampledPoints && edgeSnapshot.sampledPoints.length > 1
      ? edgeSnapshot.sampledPoints
      : [vertexA.position, vertexB.position];
    const geometry = new THREE.BufferGeometry().setFromPoints(sampledPoints.map(buildVector3));
    const featureLine = createTopologyFeatureLineProxy({
      geometry,
      curveKind: edgeSnapshot.curveKind,
      sampledPointCount: sampledPoints.length,
    });
    featureLine.setOwningResultMesh(this);
    featureLine.setFeatureContext({
      objectId: this.objectId,
      metadata: this.featureMetadata,
      edgeIndex: edgeSnapshot.index,
      subEntityId: edgeSnapshot.subEntityId,
      persistentRef: edgeSnapshot.ref,
      adjacentFaceIndices: edgeSnapshot.adjacentFaceIndices,
      adjacentFaceRefs: edgeSnapshot.owningFaceRefs,
    });
    this.featureLineMap.set(edgeIndex, featureLine);
    return featureLine;
  }

  //------ 模型显示模式 开始------
  /**
   * 同步合并拓扑显示边对象。
   *
   * 设计说明：
   * 1. 显示模式只需要默认可见黑边，不需要创建可交互 FeatureLine；
   * 2. 当拓扑快照变更时直接重建合并几何，保证边线与最新结果体一致；
   * 3. 禁用显示边时必须释放几何和材质，避免结果体替换后残留 GPU 资源。
   */
  private syncTopologyDisplayEdges(): void {
    this.disposeTopologyDisplayEdgesMesh();
    if (
      !this.topologyDisplayEdgesEnabled
      || this.topologyDisplayEdgesSuppressed
      || !this.topologySnapshot
    ) {
      return;
    }

    const displayEdgesMesh = new FeatureTopologyDisplayEdgesMesh(
      this.topologySnapshot,
      this.topologyDisplayEdgeColor,
    );
    addObject3DToParent(displayEdgesMesh, this);
    this.topologyDisplayEdgesMesh = displayEdgesMesh;
  }

  /**
   * 判断当前合并显示边对象是否与开关状态不一致。
   *
   * 设计说明：
   * 编辑特征、删除特征后的历史重放会清理并重建拓扑对象；如果显示模式本身没有变化，
   * 外部再次传入相同模式时也必须能够补建缺失的显示边，避免合并体和单独体视觉状态漂移。
   *
   * @returns 显示边对象需要重新同步时返回 true。
   */
  private isTopologyDisplayEdgesStateDirty(): boolean {
    if (this.topologyDisplayEdgesSuppressed) {
      return this.topologyDisplayEdgesMesh !== null;
    }
    if (!this.topologyDisplayEdgesEnabled) {
      return this.topologyDisplayEdgesMesh !== null;
    }
    return Boolean(this.topologySnapshot) && this.topologyDisplayEdgesMesh === null;
  }

  /**
   * 释放当前合并拓扑显示边对象。
   */
  private disposeTopologyDisplayEdgesMesh(): void {
    this.topologyDisplayEdgesMesh?.dispose();
    this.topologyDisplayEdgesMesh = null;
  }
  //------ 模型显示模式 结束------

  /**
   * 读取或创建点代理。
   *
   * @param vertexIndex 顶点索引。
   * @returns 点代理。
   */
  private ensureFeaturePointProxy(vertexIndex: number): FeaturePoint {
    const cachedPoint = this.featurePointMap.get(vertexIndex);
    if (cachedPoint) {
      return cachedPoint;
    }

    if (!this.topologySnapshot) {
      throw new Error(`结果体缺少拓扑快照，无法创建点代理: ${this.objectId}`);
    }
    const vertexSnapshot = this.topologyVertexByIndex.get(vertexIndex);
    if (!vertexSnapshot) {
      throw new Error(`未找到顶点拓扑快照，无法创建点代理: ${this.objectId}#vertex:${vertexIndex}`);
    }

    if (!shouldAttachPointProxyForVertex(vertexSnapshot)) {
      throw new Error(`曲线参考点不可创建为可选顶点代理: ${this.objectId}#vertex:${vertexIndex}`);
    }

    const featurePoint = new FeaturePoint(buildVector3(vertexSnapshot.position));
    featurePoint.setOwningResultMesh(this);
    featurePoint.setFeatureContext({
      objectId: this.objectId,
      metadata: this.featureMetadata,
      vertexIndex: vertexSnapshot.index,
      subEntityId: vertexSnapshot.subEntityId,
      persistentRef: vertexSnapshot.ref,
    });
    this.featurePointMap.set(vertexIndex, featurePoint);
    return featurePoint;
  }

  /**
   * 刷新面代理缓存里的边界线引用。
   */
  private refreshFeatureFaceBoundaryLines(): void {
    this.featureFaceMap.forEach((featureFace) => {
      /**
       * Hover 热路径只能同步已经存在的边代理。若这里调用公开的完整重绑入口，密集孔面会在
       * 每次挂载或释放单条边时隐式创建全部邻接边，形成平方级重复工作。
       */
      featureFace.setBoundaryLines(
        featureFace.adjacentEdgeIndices
          .map((edgeIndex) => this.featureLineMap.get(edgeIndex) ?? null)
          .filter((featureLine): featureLine is FeatureLine => featureLine instanceof FeatureLine),
      );
    });
  }

  /**
   * 释放单条边代理。
   *
   * @param edgeIndex 边索引。
   * @param featureLine 边代理对象。
   */
  private disposeFeatureLineProxy(edgeIndex: number, featureLine: FeatureLine): void {
    // 当前显示材质不一定是曾经使用过的全部材质，交给代理归还全部状态绑定。
    featureLine.dispose();
    this.featureLineMap.delete(edgeIndex);
  }

  /**
   * 释放单个点代理。
   *
   * @param vertexIndex 顶点索引。
   * @param featurePoint 点代理对象。
   */
  private disposeFeaturePointProxy(vertexIndex: number, featurePoint: FeaturePoint): void {
    // 点在命令接管后可能已切回默认态，必须释放其曾用于渲染的全部状态材质。
    featurePoint.dispose();
    this.featurePointMap.delete(vertexIndex);
  }

  /**
   * 释放单个面代理。
   *
   * @param faceIndex 面索引。
   * @param featureFace 面代理对象。
   */
  private disposeFeatureFaceProxy(faceIndex: number, featureFace: FeatureFace): void {
    if (featureFace.parent === this) {
      this.remove(featureFace);
    }
    /**
     * imported 组合 Provider 可在有界预算内复用不可变面几何；其它 Provider 与原生结果体
     * 没有声明接管时仍由 FeatureFace 生命周期直接释放，避免共享契约扩散到普通建模路径。
     */
    const geometryReleasedByProvider = this.featureFaceGeometryProvider
      ?.releaseFaceGeometry?.(featureFace.geometry) ?? false;
    if (!geometryReleasedByProvider) {
      featureFace.geometry.dispose();
    }
    disposeObjectMaterial(featureFace);
    this.featureFaceMap.delete(faceIndex);
  }

  /**
   * 清理面代理缓存。
   */
  private clearFeatureFaceProxyCache(): void {
    this.disposeAllFeatureFaceProxies();
  }

  /**
   * 释放所有边代理。
   */
  private disposeAllFeatureLineProxies(): void {
    this.featureLineMap.forEach((featureLine, edgeIndex) => {
      this.disposeFeatureLineProxy(edgeIndex, featureLine);
    });
  }

  /**
   * 释放所有点代理。
   */
  private disposeAllFeaturePointProxies(): void {
    this.featurePointMap.forEach((featurePoint, vertexIndex) => {
      this.disposeFeaturePointProxy(vertexIndex, featurePoint);
    });
  }

  /**
   * 释放所有面代理。
   */
  private disposeAllFeatureFaceProxies(): void {
    this.featureFaceMap.forEach((featureFace, faceIndex) => {
      this.disposeFeatureFaceProxy(faceIndex, featureFace);
    });
  }

  /**
   * 为线性阵列预览克隆当前结果体。
   *
   * 设计说明：
   * 1. 克隆 Mesh 本身及其几何与材质；
   * 2. 复制特征元数据和面三角形范围定义；
   * 3. 拓扑快照与代理缓存不克隆（运行时缓存，需要时调用 setTopologySnapshot 重新初始化）；
   * 4. 克隆体与源体是独立个体，材质/几何可复用，但需按需释放。
   *
   * @returns 克隆后的结果体。
   */
  public cloneForPatternPreview(): FeatureResultMesh {
    // 克隆几何与材质
    const clonedGeometry = this.geometry.clone();
    const clonedMaterial = Array.isArray(this.material)
      ? this.material.map((m) => m.clone())
      : this.material.clone();

    // 创建新的 FeatureResultMesh 实例
    const newResultMesh = new FeatureResultMesh({
      meshData: {
        id: this.objectId,
        metadata: { ...this.featureMetadata },
        faceTriangleRanges: this.faceTriangleRanges,
      },
      geometry: clonedGeometry,
      material: clonedMaterial,
    });

    // 复制 Mesh 的变换信息
    newResultMesh.position.copy(this.position);
    newResultMesh.quaternion.copy(this.quaternion);
    newResultMesh.scale.copy(this.scale);

    return newResultMesh;
  }
}
